import { vestResolver } from "@hookform/resolvers/vest";
import React, { FC, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import CarSimFuelTypes from "./Model/CarSimFuelTypes";
import CarSimYears from "./Model/CarSimYearModel";
import ICarSimInputData from "./Model/ICarSimInputData";
import CarSimProps from "./Model/ICarSimOutputData";
import CarSimService from "./services/CarSimService";
import { FormSuite } from './FormModel/CarSimFormSuite';
import { UseTranslationContext } from "./TranslationContext";
import ReactTooltip from 'react-tooltip';
import parse from 'html-react-parser';
import { toast } from 'react-toastify';

import { getTextFieldInputCssClass, getInputCssClass } from './FormModel/CarSimFormStyleHelper';
import { UseConfigContext } from "./ConfigContext";

import './css/normalize.css';
import './css/webflow.css';
import './css/webflow_carsim.css';
import './css/carsim.css';
import Lottie from "lottie-react";
import spinner from "./FormModel/lottie_spinner.json";
import { DateTime } from "luxon";
import { Checkbox, MenuItem, Select } from "@mui/material";



interface CarSimFormProps {
  updateData(myData: CarSimProps, selectedYear: number): void;
}

export const CarSimFormComponent: FC<CarSimFormProps> = (props) => {
  const [isProcessingRequest, setIsProcessingRequest] = useState(false);

  // TRANSLATION CONTEXT
  const { currentLanguage, changeLanguage, getKeyFromKeyId } = UseTranslationContext();

  // CONFIG CONTEXT
  const { getCarsimUrl } = UseConfigContext();

  const handleLanguageSelectChange = (event: any) => {
    changeLanguage?.(event.target.value);
  };
  // SELECT OPTIONS
  const carSimYears = new CarSimYears();
  const carSimFuelTypes = new CarSimFuelTypes();

  // REACT HOOK FORM INIT    
  const { register, handleSubmit, formState: { errors }, control, setValue } = useForm({
    resolver: vestResolver(FormSuite),
    mode: 'onChange',
    reValidateMode: 'onChange',
    context: undefined,
    criteriaMode: "firstError",
    shouldFocusError: false,
    shouldUnregister: false,
    delayError: undefined,
  });

  // REACT WATCH INIT
  const selectedIncomeYear = useWatch({ control, name: "incomeYear", defaultValue: '1' });
  const selectedFuel = useWatch({ control, name: "fuelType", defaultValue: 'diesel' });
  const falseHybrid = useWatch({ control, name: "falseHybrid", defaultValue: false });

  useEffect(() => {
    if (selectedFuel == 'electrique') {
      // setValue is used to set values in a React Form hook. the string names are registered on each component
      setValue('co2Emission', 0, { shouldValidate: true, shouldDirty: true});
      setValue('co2EmissionNew', 0);
      setValue('falseHybrid', false);
    }
  }, [selectedFuel]);

  useEffect(() => {
    if (falseHybrid == false) {
      setValue('co2EmissionNew', 0);
    }
  }, [falseHybrid]);


  // SEND FORM
  const SendCarSimForm = async (data: any) => {

    if (isProcessingRequest == true)
      return;

    // check if the user is from the Portal
    var userId = 0;
    try{
      var sessionItem = sessionStorage.getItem("user");
      if (sessionItem != null) {
        const userItem = JSON.parse(sessionStorage.getItem("user") || "")
        if (userItem != null && userItem != "") {
          if (userItem.id != null) {
            userId = parseInt(userItem.id);
          }
        }
      }
    }
    catch(e)
    {

    }

    // car sim data
    let tempValue = "01/" + data?.vehicleSeniority;
    var registrationDate = DateTime.fromFormat(tempValue, "dd/MM/yyyy");

    let tempAcqui = "01/" + (data?.VehicleAcquisitionDate !== "" ? data?.VehicleAcquisitionDate : data?.vehicleSeniority);
    var AcquiDate = DateTime.fromFormat(tempAcqui, "dd/MM/yyyy");

    
    var incomeYearFromSelect = carSimYears.years.filter(x => x.value == selectedIncomeYear)[0].label;
    let carSimData: ICarSimInputData = {
      FuelType: selectedFuel,
      CO2Emission: data?.co2Emission,
      CatalogValue: data?.catalogValue,
      FirstPlateDate: registrationDate,
      AgeMonth: 0,
      PersonalContribution: data?.personalContrib,
      IncomeYear: incomeYearFromSelect,
      // FuelCard: false,
      // AllowanceStartDate: null,
      VehicleAcquisitionDate: AcquiDate,
      CO2EmissionNew: data?.co2EmissionNew ? data?.co2EmissionNew : 0,
      FakeHybrid: falseHybrid,
      UserId: userId,
      UserLang: currentLanguage
    };

    setIsProcessingRequest(true);

    var carSimServiceData = CarSimService(carSimData, getCarsimUrl());
    carSimServiceData.then(
      Result => {
        setIsProcessingRequest(false);

        if (typeof (Result) == 'string') {
          toast(getKeyFromKeyId('ERROR_SERVER_DATA'), { type: 'error', theme: 'colored', autoClose: 5000, hideProgressBar: true, closeOnClick: true, position: 'top-center' });
        }
        else {
          return props.updateData(Result as CarSimProps, incomeYearFromSelect);
        }
      })
      .catch(e => {
        toast(getKeyFromKeyId('ERROR_SERVER_DATA'), { type: 'error', theme: 'colored', autoClose: 5000, hideProgressBar: true, closeOnClick: true, position: 'top-center' });
      });
  };

  return (
    <>
      <ReactTooltip />

      <div id="w-node-_55d6e6fd-8ced-639a-f228-73dc866008d6-85410691" className="form-block w-form carSimForm">
        {/* FORM START */}
        <form id="wf-form-Form-2" name="wf-form-Form-2" data-name="Form" method="post" className="form no-print" onSubmit={handleSubmit(SendCarSimForm)}>
          <section className="section-form">
            <h3 id="title-h3-form-section1" className="sous-titre">{getKeyFromKeyId('DATA')}</h3>

            {/* INCOME YEAR */}
            <label htmlFor="incomeSelect" className="field-label">{getKeyFromKeyId('INCOME_YEAR')}</label>
            <div className="text-field-icon">
              <select id="incomeSelect" {...register('incomeYear')} data-name="Field" className="select-field w-select">
                {
                  carSimYears.years.map((option, index) => (
                    <option value={option.value} key={"year" + index}>{option.label}</option>
                  ))
                }
              </select>
            </div>

            {/* FUEL TYPE */}
            <label htmlFor="selectFuel" className="field-label">{getKeyFromKeyId('FUEL_TYPE')}</label>
            <div className="text-field-icon">
              <select id="selectFuel" {...register("fuelType")} data-name="Field 3" className="select-field w-select">
                {
                  carSimFuelTypes.fuelTypes.map((option, index) => (
                    <option value={option.value} key={"fuel" + index}>{getKeyFromKeyId(option.key)}</option>
                  ))
                }
              </select>
            </div>

            {/* FALSE HYBRID */}
            <div className="div-checkbox">
            <label className="w-checkbox checkbox-field" htmlFor="falseHybrid">
            <Checkbox size="medium"
                disabled={selectedFuel == 'electrique' ? true : false} id="falseHybrid" data-name="falseHybrid" checked={falseHybrid} {...register('falseHybrid')}
              sx={{
              color: '#90BBB5',
                '&.Mui-checked': {
                color: '#90BBB5',
              }
            }} />
              <span className="w-form-label">{getKeyFromKeyId('FALSE_HYBRID')}</span></label>
              <button type="button" aria-label={getKeyFromKeyId("ICON_TOOLTIP")} tabIndex={0} className="ic-info-button no-print w-button" onFocus={(e) => ReactTooltip.show(e.target)} onBlur={(e) => ReactTooltip.hide(e.target)} data-for="TooltipHtml" data-type='info' data-tip={getKeyFromKeyId('FALSE_HYBRID_INFO_MULTILINE')}></button>
            </div> 

            {/* CATALOG VALUE */}
            <div className="group-field-icon">
              <div className="group-label-icon">
                <label htmlFor="catalogValue" className="field-label">{getKeyFromKeyId('CATALOG_VALUE_VAT')} *</label>
                <button type="button" aria-label={getKeyFromKeyId("ICON_TOOLTIP")} tabIndex={0} className="ic-info-button no-print w-button" onFocus={(e) => ReactTooltip.show(e.target)} onBlur={(e) => ReactTooltip.hide(e.target)} data-for="TooltipHtml" data-type='info' data-tip={getKeyFromKeyId('TOOLTIP_INFO_CATALOG_VALUE_MULTILINE')}></button>
              </div>
              <div className={getTextFieldInputCssClass(errors?.catalogValue)}>
                <input type="number" min={0} step=".01" {...register("catalogValue")} className={getInputCssClass(errors?.catalogValue)} maxLength={256} name="catalogValue" data-name="catalogValue" placeholder="0 €" id="catalogValue"></input>
              </div>
              {errors?.catalogValue && <p className='inputErrorHelper'>{getKeyFromKeyId(errors.catalogValue.message)}</p>}
            </div>

            {/* CO2 EMISSION */}
            <div className="group-field-icon">
              <div className="group-label-icon">
                <label htmlFor="co2Emission" className="field-label">{parse(getKeyFromKeyId('CO2_EMISSION'))} *</label>
                <button type="button" aria-label={getKeyFromKeyId("ICON_TOOLTIP")} tabIndex={0} className="ic-info-button no-print w-button" onFocus={(e) => ReactTooltip.show(e.target)} onBlur={(e) => ReactTooltip.hide(e.target)} data-for="TooltipHtml" data-type='info' data-tip={getKeyFromKeyId('TOOLTIP_INFO_CO2_EMISSION_MULTILINE')}></button>
              </div>
              <div className={getTextFieldInputCssClass(errors?.co2Emission)}>
                <input type="number" disabled={selectedFuel == 'electrique' ? true : false} min={0} step=".01" {...register("co2Emission")} className={getInputCssClass(errors?.co2Emission)} maxLength={256} name="co2Emission" data-name="co2Emission" placeholder="0 gr" id="co2Emission"></input>
              </div>
              {errors?.co2Emission && <p className='inputErrorHelper'>{getKeyFromKeyId(errors.co2Emission.message)}</p>}
            </div>

            {/* CO2 EMISSION NEW */}
            {
              falseHybrid == true &&
              <>
                  <div>
                <label htmlFor="co2EmissionNew" className="field-label">{getKeyFromKeyId('CO2_EMISSION_NEW')} (g/km) *</label>
                <div className={getTextFieldInputCssClass(errors?.co2EmissionNew)}>
                  <input type="number" min={0} step=".01" {...register("co2EmissionNew")} className={getInputCssClass(errors?.co2EmissionNew)} maxLength={256} data-name="Field 4" placeholder="0 gr" id="co2EmissionNew"></input>
                </div>
                {errors?.co2EmissionNew && <p className='inputErrorHelper'>{getKeyFromKeyId(errors.co2EmissionNew.message)}</p>}
                </div>
              </>
            }

            {/* PERSONAL CONTRIBUTION */}
            <label htmlFor="personalId" className="field-label">{getKeyFromKeyId('PERSONAL_INTERVENTION')}</label>
            <div className={getTextFieldInputCssClass(errors?.personalContrib)}>
              <input type="number" min={0} step=".01" defaultValue='0' className={getInputCssClass(errors?.personalContrib)} maxLength={256} data-name="Field 4" placeholder="0 €" id="personalId" {...register("personalContrib")}></input>
            </div>
            {errors?.personalContrib && <p className='inputErrorHelper'>{getKeyFromKeyId(errors.personalContrib.message)}</p>}
          </section>

          <section id="w-node-_4c3c0e88-4806-5a8d-e12e-91878d3575ca-a2169bf1" className="section-form-date">
            <h3 id="title-h3-form-section2" className="sous-titre">{getKeyFromKeyId('VEHICLE_SENIORITY')}</h3>

            {/* REGISTRATION DATE*/}
            <label htmlFor="registrationDate" className="field-label">{getKeyFromKeyId('DATE_1ST_DIV_REGISTRATION_MM_YYYY')} *</label>
            <div className={getTextFieldInputCssClass(errors?.vehicleSeniority)}>
              <input type="text" {...register("vehicleSeniority")} className={getInputCssClass(errors?.vehicleSeniority)} maxLength={256} placeholder={getKeyFromKeyId("M_Y_PLACEHOLDER")} id="registrationDate"></input>
            </div>
            {errors?.vehicleSeniority && <p className='inputErrorHelper'>{getKeyFromKeyId(errors.vehicleSeniority.message)}</p>}

            {/* ACQUISITION DATE*/}
            <div className="group-label-icon">
              <label htmlFor="VehicleAcquisitionDate" className="field-label">{getKeyFromKeyId('DATE_ACQUISITION')} * (¹)</label>
              <button type="button" aria-label={getKeyFromKeyId("ICON_TOOLTIP")} tabIndex={0} className="ic-info-button no-print w-button" onFocus={(e) => ReactTooltip.show(e.target)} onBlur={(e) => ReactTooltip.hide(e.target)} data-for="TooltipHtml" data-type='info' data-tip={getKeyFromKeyId('TOOLTIP_ACQUISITION')}></button>
            </div>
            
            <div className={getTextFieldInputCssClass(errors?.VehicleAcquisitionDate)}>
              <input type="text" {...register("VehicleAcquisitionDate")} className={getInputCssClass(errors?.VehicleAcquisitionDate)} maxLength={256} placeholder={getKeyFromKeyId("M_Y_PLACEHOLDER")} id="VehicleAcquisitionDate"></input>
            </div>
            {errors?.VehicleAcquisitionDate && <p className='inputErrorHelper'>{getKeyFromKeyId(errors.VehicleAcquisitionDate.message)}</p>}

          </section>

         

          {/* SUBMIT */}
          {/*}
        <div className="submit-button">
              <div id="w-node-c0c5bcd2-7366-4d09-6def-e688e4986d90-7117b8a8" className="textbouton">{getKeyFromKeyId('CALCULATE')}</div>
              <div className="lottie-animation" data-w-id="22208741-ce0e-c6b9-f542-95390b845ced" data-animation-type="lottie" data-src="js/lottie_spinner.json" data-loop="1" data-direction="1" data-autoplay="1" data-is-ix2-target="0" data-renderer="svg" data-default-duration="2.0020019204587935" data-duration="0"></div>
            </div>

            <div className="submit-button">
              <div id="w-node-c0c5bcd2-7366-4d09-6def-e688e4986d90-7117b8a8" className="textbouton">{getKeyFromKeyId('CALCULATE')}</div>
              <Lottie animationData={spinner} autoPlay={true} loop={true} className="lottie-animation" />
            </div>
        */}
          {
            <button formNoValidate={true} type="submit" className="submit-button w-node-c0c5bcd2-7366-4d09-6def-e688e4986d90-7117b8a8 no-print">
              <div className={isProcessingRequest == false?"textbouton":"textbouton buttonSpinnerMargin"}>{getKeyFromKeyId('CALCULATE')} </div>
              {isProcessingRequest &&
                <Lottie animationData={spinner} autoPlay={true} loop={true} className="lottie-animation" />}
            </button>
          }

          <div id="w-node-c0c5bcd2-7366-4d09-6def-e688e4986d90-7117b8a8" className="no-print left">* {getKeyFromKeyId('MANDATORY_FIELDS')}</div>
          <div className="no-print left"><p className="legende-2">(¹) {getKeyFromKeyId('ERROR_ACQUISITION')}</p></div>
          
        </form>

        {/* FORM STATE DISPLAY */}
        {/*
      <div className="success-message w-form-done">
        <div>Thank you! Your submission has been received!</div>
      </div>
      <div className="w-form-fail">
        <div>Oops! Something went wrong while submitting the form.</div>
      </div>
      */}
      </div>
    </>
  );

}

{
  /*UNUSED HANDLERS*/


  /*

    const handleCatalogValueChange = (event : any) => {
      setCatalogValue(event.target.value as number);
    };

    const handleCO2ValueChange = (event : any) => {
      setCO2Value(event.target.value);
    };

    const handlePersonalContribChange = (event : any) => {
      setPersonalContribValue(event.target.value);
    };

    const handleCo2EmissionNewChange = (event : any) => {
      setCO2NewValue(event.target.value);
    };


  */

}